import React, { type ReactNode } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { Box, Chip, SxProps, Theme } from '@mui/material';

type NuggetChipProps = Readonly<{
  id?: string;
  label: ReactNode;
  /** Shown in the leading circle. Falls back to `icon` when there's no image. */
  image?: Readonly<{ src: string; alt?: string }>;
  icon?: ReactNode;
  onClick?: () => void;
  onRemove?: () => void;
  /** Set when the chip sits in a run of text rather than a flex row. */
  inline?: boolean;
  /** `sm` is the compact badge; `xl` is the heading-sized one, with larger text and image. */
  size?: 'sm' | 'lg' | 'xl';
  sx?: SxProps<Theme>;
}>;

export const NuggetChip: React.FC<NuggetChipProps> = ({
  id,
  label,
  image,
  icon,
  onClick,
  onRemove,
  inline,
  size = 'lg',
  sx,
}) => {
  const leading = image ? (
    <Box component="img" src={image.src} alt={image.alt ?? ''} sx={imageSx} />
  ) : (
    icon
  );

  return (
    <Chip
      id={id}
      component={inline ? 'span' : 'div'}
      variant="badge"
      type="leading"
      size={size === 'sm' ? 'sm' : 'lg'}
      clickable={Boolean(onClick)}
      label={label}
      icon={leading ? <Box sx={circleSx}>{leading}</Box> : undefined}
      onClick={onClick}
      onDelete={onRemove}
      deleteIcon={
        onRemove ? (
          <Box component="span" sx={deleteIconSx}>
            <AppIcon iconName="XClose" diameter="12px" color="Gray/400" />
          </Box>
        ) : undefined
      }
      sx={
        [
          chipSx,
          Boolean(onClick) && clickableSx,
          Boolean(inline) && inlineSx,
          size === 'sm' && smSx,
          size === 'xl' && xlSx,
          ...(Array.isArray(sx) ? sx : [sx]),
        ] as SxProps<Theme>
      }
    />
  );
};

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  paddingLeft: backrs.spacing['spacing-md'],
  // the UI kit badge is 8/10; there is no 10px step on the spacing scale
  paddingRight: '10px',
  '& .MuiChip-label': {
    color: backrs.colors['Colors/Text/text-secondary'],
    padding: 0,
  },
  '& .MuiChip-icon': {
    margin: 0,
  },
  '& .MuiChip-deleteIcon': {
    margin: 0,
  },
});

const clickableSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
    opacity: 0.7,
  },
});

const smSx: SxProps<Theme> = ({ backrs }) => ({
  paddingLeft: backrs.spacing['spacing-xs'],
  paddingRight: backrs.spacing['spacing-sm'],
  borderRadius: backrs.radius['radius-sm'],
});

const xlSx: SxProps<Theme> = ({ backrs, typography }) => ({
  gap: backrs.spacing['spacing-sm'],
  paddingLeft: backrs.spacing['spacing-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  '& .MuiChip-label': {
    ...typography['text-md-medium'],
    color: backrs.colors['Colors/Text/text-secondary'],
    padding: 0,
  },
  '& .MuiChip-icon > *': {
    width: '24px',
    height: '24px',
  },
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  },
});

const inlineSx: SxProps<Theme> = {
  display: 'inline-flex',
  width: 'auto',
  maxWidth: '100%',
  verticalAlign: 'middle',
};

const circleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
  width: '16px',
  height: '16px',
  borderRadius: backrs.radius['radius-full'],
  overflow: 'hidden',
});

const imageSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const deleteIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  cursor: 'pointer',
  padding: backrs.spacing['spacing-xxs'],
  borderRadius: backrs.radius['radius-xs'],
});
